import { ChatTransformer } from '../../services'
import { getContactActivityTimestamp } from '../../utils/sidebarListSort'
import type { ChatContactWithChat } from '../../types'
import type { IMessage } from '../../types/IChat'
import type { ChatOut } from '../../types'

function parsePinnedAt(value?: string | null): number {
  if (!value?.trim()) {
    return 0
  }

  const ms = Date.parse(value)
  return Number.isNaN(ms) ? 0 : ms
}

function comparePinnedThenActivity(a: ChatContactWithChat, b: ChatContactWithChat): number {
  const aPinned = Boolean(a.chat?.is_pinned)
  const bPinned = Boolean(b.chat?.is_pinned)
  if (aPinned !== bPinned) {
    return aPinned ? -1 : 1
  }

  if (aPinned && bPinned) {
    const pinnedDiff = parsePinnedAt(b.chat?.pinned_at) - parsePinnedAt(a.chat?.pinned_at)
    if (pinnedDiff !== 0) {
      return pinnedDiff
    }
  }

  return getContactActivityTimestamp(b) - getContactActivityTimestamp(a)
}

/** Сортирует список: сначала закреплённые, внутри — pinned_at / activity. */
export function sortChatsByLastMessage(
  chats: ChatContactWithChat[],
): ChatContactWithChat[] {
  return [...chats].sort((a, b) => {
    const primary = comparePinnedThenActivity(a, b)
    if (primary !== 0) {
      return primary
    }

    const aId = String(a.chat?.id ?? a.id)
    const bId = String(b.chat?.id ?? b.id)
    if (aId === bId) {
      return 0
    }

    const aNumeric = Number(aId)
    const bNumeric = Number(bId)
    const bothNumeric = Number.isFinite(aNumeric) && Number.isFinite(bNumeric)
    if (bothNumeric) {
      return bNumeric - aNumeric
    }

    return bId.localeCompare(aId)
  })
}

function contactKey(contact: ChatContactWithChat): string {
  return String(contact.chat?.id ?? contact.id)
}

/** Обновляет контакты, сохраняя порядок уже показанного списка. */
export function mergeContactsPreservingOrder(
  previous: ChatContactWithChat[],
  next: ChatContactWithChat[],
): ChatContactWithChat[] {
  const nextById = new Map(next.map(contact => [contactKey(contact), contact]))
  const ordered: ChatContactWithChat[] = []

  for (const prev of previous) {
    const updated = nextById.get(contactKey(prev))
    if (!updated) {
      continue
    }
    ordered.push(updated)
    nextById.delete(contactKey(prev))
  }

  if (nextById.size === 0) {
    return ordered
  }

  return [...ordered, ...sortChatsByLastMessage([...nextById.values()])]
}

/** Закреплённые сверху; порядок незакреплённых не трогаем. */
export function ensurePinnedGrouping(
  contacts: ChatContactWithChat[],
): ChatContactWithChat[] {
  const pinned: ChatContactWithChat[] = []
  const unpinned: ChatContactWithChat[] = []

  for (const contact of contacts) {
    if (contact.chat?.is_pinned) {
      pinned.push(contact)
    } else {
      unpinned.push(contact)
    }
  }

  pinned.sort((a, b) => parsePinnedAt(b.chat?.pinned_at) - parsePinnedAt(a.chat?.pinned_at))
  return [...pinned, ...unpinned]
}

/** Поднимает чат сразу под закреплёнными (новое сообщение / отправка). */
export function moveContactToUnpinnedTop(
  contacts: ChatContactWithChat[],
  chatId: number | string,
): ChatContactWithChat[] {
  const targetKey = String(chatId)
  const index = contacts.findIndex(contact => contactKey(contact) === targetKey)
  if (index === -1) {
    return contacts
  }

  const target = contacts[index]
  if (target.chat?.is_pinned) {
    return ensurePinnedGrouping(contacts)
  }

  const without = contacts.filter((_, i) => i !== index)
  let insertAt = 0
  while (insertAt < without.length && without[insertAt].chat?.is_pinned) {
    insertAt += 1
  }

  return [
    ...without.slice(0, insertAt),
    target,
    ...without.slice(insertAt),
  ]
}

export function findContactIndexByChatId(
  contacts: ChatContactWithChat[],
  chatId: number | string,
): number {
  const normalizedChatId = String(chatId)

  return contacts.findIndex(contact =>
    String(contact.id) === normalizedChatId
    || String(contact.chat?.id ?? '') === normalizedChatId,
  )
}

export function buildLastMessagePreview(message: IMessage) {
  return ChatTransformer.messageToLastMessage(message)
}

/** Проставляет lastMessage на UI-чат из IMessage / last_message core. */
export function applyCoreLastMessageToChatOut(
  chatOut: ChatOut,
  lastMsg: {
    content?: string | null
    created_at?: string | null
    created_by?: { id?: string | number } | null
    is_read?: boolean
    attachments?: ChatOut['lastMessage']['attachments']
  },
): void {
  chatOut.lastMessage = {
    message: lastMsg.content || '',
    time: lastMsg.created_at || '',
    senderId: Number(lastMsg.created_by?.id || 0),
    feedback: {
      isSent: true,
      isDelivered: true,
      isSeen: lastMsg.is_read === true,
    },
    attachments: lastMsg.attachments || [],
  }
}

export function dedupeContactsById(
  contacts: ChatContactWithChat[],
): ChatContactWithChat[] {
  return contacts.filter((contact, index) =>
    contacts.findIndex(c => c.id === contact.id) === index,
  )
}
